/* eslint-disable @typescript-eslint/no-unused-vars, @typescript-eslint/require-await */
import { getWallets } from '@wallet-standard/app';
import { PresetsUtil } from '@reown/appkit-utils';
import { bitcoin, bitcoinTestnet } from '@reown/appkit/networks';
import { MethodNotSupportedError } from '../errors/MethodNotSupportedError.js';
import { AddressPurpose } from '../utils/BitcoinConnector.js';
import { ProviderEventEmitter } from '../utils/ProviderEventEmitter.js';
export class WalletStandardConnector extends ProviderEventEmitter {
    constructor({ wallet, requestedChains }) {
        super();
        this.chain = 'bip122';
        this.type = 'ANNOUNCED';
        this.requestedChains = [];
        this.walletUnsubscribes = [];
        this.provider = this;
        this.wallet = wallet;
        this.requestedChains = requestedChains;
        this.provider = this;
    }
    get id() {
        return this.name;
    }
    get name() {
        return this.wallet.name;
    }
    get imageUrl() {
        return this.wallet.icon;
    }
    get explorerId() {
        return PresetsUtil.ConnectorExplorerIds[this.name];
    }
    get chains() {
        return this.wallet.chains
            .map(chainId => this.requestedChains.find(chain => {
            switch (chainId) {
                case 'bitcoin:mainnet':
                    return chain.caipNetworkId === bitcoin.caipNetworkId;
                case 'bitcoin:testnet':
                    return chain.caipNetworkId === bitcoinTestnet.caipNetworkId;
                default:
                    return chain.caipNetworkId === chainId;
            }
        }))
            .filter(Boolean);
    }
    async connect() {
        const connectFeature = this.getWalletFeature('bitcoin:connect');
        this.bindEvents();
        const response = await connectFeature.connect({ purposes: ['payment', 'ordinals'] });
        const account = response.accounts[0];
        if (!account) {
            throw new Error('No account found');
        }
        return account.address;
    }
    async getAccountAddresses() {
        const addresses = new Set();
        const mappedAccounts = this.wallet.accounts
            .map(acc => ({
            address: acc.address,
            purpose: AddressPurpose.Payment,
            publicKey: Buffer.from(acc.publicKey).toString('hex')
        }))
            .filter(acc => {
            if (addresses.has(acc.address)) {
                return false;
            }
            addresses.add(acc.address);
            return true;
        });
        return Promise.resolve(mappedAccounts);
    }
    async signMessage(params) {
        if (params.protocol) {
            console.warn('WalletStandardConnector:signMessage - protocol parameter not supported in WalletStandard:bitcoin - signMessage');
        }
        const feature = this.getWalletFeature('bitcoin:signMessage');
        const account = this.wallet.accounts.find(acc => acc.address === params.address);
        if (!account) {
            throw new Error('Account not found');
        }
        const message = new TextEncoder().encode(params.message);
        const response = (await feature.signMessage({ account, message }))[0];
        if (!response) {
            throw new Error('No response from wallet');
        }
        return Buffer.from(response.signature).toString('base64');
    }
    async signPSBT(params) {
        const feature = this.getWalletFeature('bitcoin:signTransaction');
        if (params.broadcast) {
            throw new MethodNotSupportedError(this.id, 'signPSBT', 'This wallet does not support broadcasting, please broadcast it manually or contact the development team.');
        }
        const inputsToSign = params.signInputs.map(input => {
            const account = this.wallet.accounts.find(acc => acc.address === input.address);
            if (!account) {
                throw new Error(`Account with address ${input.address} not found`);
            }
            return {
                account,
                signingIndexes: [input.index],
                sigHash: undefined
            };
        });
        const response = (await feature.signTransaction({
            psbt: new Uint8Array(Buffer.from(params.psbt, 'base64')),
            inputsToSign
        }))[0];
        if (!response) {
            throw new Error('No response from wallet');
        }
        return {
            psbt: Buffer.from(response.signedPsbt).toString('base64'),
            txid: undefined
        };
    }
    async sendTransfer(_params) {
        return Promise.reject(new MethodNotSupportedError(this.id, 'sendTransfer', 'Please use "signPSBT" instead and broadcast the transaction manually.'));
    }
    async disconnect() {
        this.unbindEvents();
        return Promise.resolve();
    }
    async request(_args) {
        return Promise.reject(new MethodNotSupportedError(this.id, 'request'));
    }
    getWalletFeature(feature) {
        if (!(feature in this.wallet.features)) {
            throw new MethodNotSupportedError(this.id, feature);
        }
        return this.wallet.features[feature];
    }
    bindEvents() {
        this.unbindEvents();
        try {
            const feature = this.getWalletFeature('standard:events');
            this.walletUnsubscribes.push(feature.on('change', data => {
                if ('accounts' in data && data.accounts) {
                    if (data.accounts.length === 0) {
                        this.emit('disconnect');
                    }
                    else {
                        this.emit('accountsChanged', data.accounts.map(acc => acc.address));
                    }
                }
            }));
        }
        catch {
            console.warn(`WalletStandardConnector:bindEvents - wallet provider "${this.name}" does not support events`);
        }
    }
    unbindEvents() {
        this.walletUnsubscribes.forEach(unsubscribe => unsubscribe());
        this.walletUnsubscribes = [];
    }
    static watchWallets({ callback, requestedChains }) {
        const { get, on } = getWallets();
        function wrapWallets(wallets) {
            // Must replace the filter with the correct function to identify bitcoin wallets
            return wallets
                .filter(wallet => 'bitcoin:connect' in wallet.features)
                .map(wallet => new WalletStandardConnector({ wallet, requestedChains }));
        }
        callback(...wrapWallets(get()));
        return on('register', (...wallets) => callback(...wrapWallets(wallets)));
    }
    async switchNetwork(_caipNetworkId) {
        throw new Error(`${this.name} wallet does not support network switching`);
    }
}
//# sourceMappingURL=WalletStandardConnector.js.map